import {Matrix4, Vector3} from 'three';
import {xrStore} from 'lib';

type RotateType = {
  previousAngle: number | null;
  viewerMatrixHelper: Matrix4;
  pos1Helper: Vector3;
  pos2Helper: Vector3;
  run: () => Promise<number | undefined>;
  reset: () => void;
};

/**
 * Detects 2 fingers clockwise/anti-clockwise rotation.
 * @returns Angle in radians.
 */

const rotate: RotateType = {
  previousAngle: null,
  viewerMatrixHelper: new Matrix4(),
  pos1Helper: new Vector3(),
  pos2Helper: new Vector3(),
  async run() {
    const frame = await xrStore.requestFrame();
    const inputSources = xrStore.getState().session?.inputSources;
    if (!inputSources || inputSources.length !== 2 || !inputSources[0] || !inputSources[1]) {
      this.previousAngle = null;
      return;
    }

    const baseSpace = xrStore.getState().originReferenceSpace;
    if (!baseSpace) {
      return;
    }

    const space1 = inputSources[0].targetRaySpace;
    const space2 = inputSources[1].targetRaySpace;

    const pose1 = frame.getPose(space1, baseSpace);
    const pose2 = frame.getPose(space2, baseSpace);

    const pos1 = pose1?.transform?.position;
    const pos2 = pose2?.transform?.position;
    if (!pos1 || !pos2) {
      return;
    }

    const viewerPose = frame.getViewerPose(baseSpace);
    if (!viewerPose) {
      return;
    }

    this.viewerMatrixHelper = this.viewerMatrixHelper.fromArray(viewerPose.transform.matrix);
    this.viewerMatrixHelper = this.viewerMatrixHelper.invert();

    this.pos1Helper.set(pos1.x, pos1.y, pos1.z);
    this.pos2Helper.set(pos2.x, pos2.y, pos2.z);

    this.pos1Helper.applyMatrix4(this.viewerMatrixHelper);
    this.pos2Helper.applyMatrix4(this.viewerMatrixHelper);

    const currentAngle = getAngleRadBetweenTouches(this.pos1Helper, this.pos2Helper);

    if (this.previousAngle !== null) {
      const deltaAngle = currentAngle - this.previousAngle;
      this.previousAngle = currentAngle;
      return deltaAngle;
    } else {
      this.previousAngle = currentAngle;
    }
  },
  reset() {
    this.previousAngle = null;
  },
};

export default rotate;

const getAngleRadBetweenTouches = (pos1: Vector3, pos2: Vector3) => {
  const dx = pos2.x - pos1.x;
  const dy = pos2.y - pos1.y;
  return Math.atan2(dy, dx);
};
